import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import client from "../lib/api";
import { Cpu, KeyRound, ShieldCheck, Lock, Globe, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Inner() {
  const { tenant } = useAuth();
  const { data, loading } = useFetch("/llm/settings");
  const [provider, setProvider] = useState("anthropic");
  const [useByok, setUseByok] = useState(false);
  const [byokKey, setByokKey] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.settings) {
      setProvider(data.settings.provider || "anthropic");
      setUseByok(!!data.settings.use_byok);
      setWorkspaceId(data.settings.byok_workspace_id || "");
    }
  }, [data]);

  if (loading) return <Loading />;
  const providers = data?.providers || {};

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/llm/settings", {
        provider, model: providers[provider]?.model, use_byok: useByok,
        byok_key: byokKey || undefined,
        byok_workspace_id: workspaceId,
      });
      toast.success("AI settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Configuration" title="AI & Security Settings" sub="Choose your intelligence engine and review data protection" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <GlassCard className="lg:col-span-2 p-6">
          <div className="flex items-center gap-2 mb-4"><div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><Cpu className="w-4 h-4 text-white" /></div><h3 className="font-bold text-slate-900">Intelligence engine</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {Object.entries(providers).map(([key, p]) => (
              <button key={key} onClick={() => setProvider(key)} data-testid={`provider-${key}`}
                className={`text-left p-4 rounded-2xl border-2 pm-transition ${provider === key ? "border-cyan-500 bg-cyan-50/60" : "border-slate-200 bg-white/70 hover:bg-white hover:border-slate-300"}`}>
                <div className="flex items-center justify-between"><span className="text-sm font-bold text-slate-800">{p.label}</span>{provider === key && <CheckCircle2 className="w-4 h-4 text-cyan-600" />}</div>
                <div className="text-[11px] text-slate-400 mt-1 font-mono">{p.model}</div>
              </button>
            ))}
          </div>
          <div className="p-4 rounded-2xl bg-white/50 border border-white/60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-600" /><div><div className="text-sm font-semibold text-slate-800">Bring your own key (BYOK)</div><div className="text-xs text-slate-500">Use your own provider API key for full data control</div></div></div>
              <Switch checked={useByok} onCheckedChange={setUseByok} data-testid="byok-toggle" />
            </div>
            {useByok && (
              <div className="mt-3 space-y-3">
                <div>
                  <Input type="password" placeholder={data?.settings?.byok_key_masked || "Paste your API key"} value={byokKey} onChange={(e) => setByokKey(e.target.value)} data-testid="byok-key" className="bg-white/70 rounded-xl font-mono" />
                  <p className="text-xs text-slate-400 mt-1.5">Stored encrypted per-tenant. Prompts are never used to train models.</p>
                </div>
                {provider === "anthropic" && (
                  <div>
                    <Input value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} data-testid="byok-workspace-id" placeholder="Anthropic Workspace ID (only for identity-linked keys)" className="bg-white/70 rounded-xl font-mono" />
                    <p className="text-xs text-slate-400 mt-1.5">
                      Required only if your Anthropic key is an <span className="font-semibold">identity-linked</span> key. Find it in the Anthropic Console → Settings → Workspaces (looks like <span className="font-mono">wrkspc_…</span>). Leave blank for standard keys.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-llm-settings" className="mt-5 rounded-xl pm-gradient text-white border-0 h-11">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}</Button>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-4 h-4 text-emerald-600" /><h3 className="font-bold text-slate-900">Security posture</h3></div>
          <div className="space-y-3">
            {[[Lock, "Encryption", "AES-256 at rest · TLS 1.3 in transit"],
              [Globe, "Data residency", tenant?.data_residency || "US"],
              [ShieldCheck, "Compliance", "SOC 2 · ISO 27001 · GDPR · HIPAA"],
              [KeyRound, "Access control", "RBAC + ABAC · SSO ready"]].map(([Icon, t, s], i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/50 border border-white/60">
                <Icon className="w-4 h-4 text-emerald-600 mt-0.5" />
                <div><div className="text-sm font-semibold text-slate-800">{t}</div><div className="text-xs text-slate-500">{s}</div></div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

export default function SettingsPage() { return <NeedTenant><Inner /></NeedTenant>; }
